import { useState } from 'react';

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-5 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors cursor-pointer text-left"
      >
        <div className="flex items-center gap-4">
          <span className="w-8 h-8 flex items-center justify-center bg-[#12335f] text-white text-sm font-bold rounded-full flex-shrink-0">
            Q
          </span>
          <span className="text-sm lg:text-base font-medium text-gray-800">{question}</span>
        </div>
        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 ml-4">
          <i className={`ri-arrow-down-s-line text-xl text-gray-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}></i>
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}>
        <div className="px-6 pb-5 pt-2 bg-[#f8fafc]">
          <div className="flex gap-4">
            <span className="w-8 h-8 flex items-center justify-center bg-[#9a1e23] text-white text-sm font-bold rounded-full flex-shrink-0">
              A
            </span>
            <p className="text-sm text-gray-700 leading-relaxed">{answer}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BottomCTA() {
  const [formData, setFormData] = useState({
    companyName: '',
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [activeForm, setActiveForm] = useState<'diagnosis' | 'consultation' | null>(null);

  const handleSubmit = async (e: React.FormEvent, formType: 'diagnosis' | 'consultation') => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const submitUrl = formType === 'diagnosis' 
      ? 'https://readdy.ai/api/form/d659k39of4o2fjfo9fdg'
      : 'https://readdy.ai/api/form/d659k39of4o2fjfo9fe0';
    
    try {
      const params = new URLSearchParams();
      params.append('companyName', formData.companyName);
      params.append('name', formData.name);
      params.append('email', formData.email);
      params.append('phone', formData.phone);
      params.append('message', formData.message);
      params.append('formType', formType === 'diagnosis' ? '無料DX推進診断' : '無料相談');

      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ companyName: '', name: '', email: '', phone: '', message: '' });
        setTimeout(() => {
          setActiveForm(null);
          setSubmitStatus('idle');
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const areas = [
    {
      region: '四国エリア',
      prefectures: ['高知県', '香川県', '愛媛県', '徳島県']
    },
    {
      region: '中国エリア',
      prefectures: ['広島県広島市', '岡山県岡山市']
    },
    {
      region: 'その他エリア',
      prefectures: ['兵庫県神戸市', '大阪府', '東京都']
    }
  ];

  const faqs = [
    {
      question: 'DXとは何ですか？',
      answer: 'DX（デジタルトランスフォーメーション）とは、デジタル技術を活用してビジネスモデルや業務プロセスを変革し、競争優位性を確立することです。単なるIT化ではなく、お客様の体験価値向上や新しい価値創造を目指します。'
    },
    {
      question: 'ITスキルがなくても大丈夫ですか？',
      answer: 'もちろん大丈夫です。むしろITスキルがない方こそ、私たちのサービスが最も価値を発揮します。専門用語を使わず、わかりやすい言葉で丁寧にサポートいたしますので、安心してご相談ください。'
    },
    {
      question: 'どのくらいの期間で効果が出ますか？',
      answer: '企業様の状況や目標によって異なりますが、多くの場合、3ヶ月程度で具体的な変化を実感いただけます。小さな成功体験を積み重ねながら、段階的に成果を出していくアプローチを取っています。'
    },
    {
      question: '費用はどのくらいかかりますか？',
      answer: 'お客様の課題や規模に応じて最適なプランをご提案いたします。まずは無料相談で現状をお聞かせください。予算に合わせた柔軟なプランニングが可能です。'
    },
    {
      question: '対面とオンライン、どちらでも対応可能ですか？',
      answer: 'はい、両方対応可能です。高知県内であれば対面での伴走支援を、遠方のお客様にはオンラインでのサポートを提供しています。お客様のご都合に合わせて最適な方法をお選びいただけます。'
    },
    {
      question: '契約期間の縛りはありますか？',
      answer: '最低契約期間は6ヶ月からとなっております。DXの取り組みは一定期間継続することで効果が現れるため、まずは6ヶ月間しっかりとサポートさせていただきます。その後は、お客様の状況に応じて柔軟に対応いたします。'
    }
  ];

  return (
    <>
      {/* CTA Section */}
      <section id="contact" className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#12335f] mb-8">
            まずはお気軽にご相談ください
          </h2>
          <p className="text-base lg:text-lg text-gray-700 mb-4 leading-relaxed">
            DXは一気に進める必要はありません。
          </p>
          <p className="text-lg lg:text-xl text-[#12335f] font-bold mb-12 leading-relaxed">
            まずは現状を整理することから始めましょう。
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <a
              href="https://digipop.co.jp/webservice/dx-diagnosis/"
              target="_blank"
              rel="noopener noreferrer"
              className="px-10 py-6 bg-[#12335f] text-white text-xl font-bold rounded-xl hover:bg-[#153577] transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center shadow-xl hover:shadow-2xl hover:-translate-y-1"
            >
              <div className="w-8 h-8 flex items-center justify-center mr-3">
                <i className="ri-search-eye-line text-2xl"></i>
              </div>
              無料DX推進診断をする
            </a>
            <a
              href="https://imachika.com/viewform/?form_id=698b466b23679c3f7a84ae56"
              target="_blank"
              rel="noopener noreferrer"
              className="px-10 py-6 bg-[#9a1e23] text-white text-xl font-bold rounded-xl hover:bg-[#7d181c] transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center shadow-xl hover:shadow-2xl hover:-translate-y-1"
            >
              <div className="w-8 h-8 flex items-center justify-center mr-3">
                <i className="ri-mail-send-line text-2xl"></i>
              </div>
              無料相談する
            </a>
            <a
              href="https://lin.ee/Xm9zGrPi"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="px-6 py-4 bg-[#06C755] text-white text-base font-medium rounded-lg hover:bg-[#05b34c] transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center"
            >
              <div className="w-5 h-5 flex items-center justify-center mr-2">
                <i className="ri-line-fill text-lg"></i>
              </div>
              LINEでお問い合わせ
            </a>
            <a
              href="https://www.chatwork.com/candypop0124"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="px-6 py-4 bg-[#ea5614] text-white text-base font-medium rounded-lg hover:bg-[#d14a10] transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center"
            >
              <div className="w-5 h-5 flex items-center justify-center mr-2">
                <i className="ri-chat-3-line text-lg"></i>
              </div>
              ChatWorkでお問い合わせ
            </a>
          </div>
        </div>
      </section>

      {/* Form Modal */}
      {activeForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 lg:p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-[#12335f]">
                  {activeForm === 'diagnosis' ? '無料DX推進診断' : '無料相談'}
                </h3>
                <button
                  onClick={() => {
                    setActiveForm(null);
                    setSubmitStatus('idle');
                  }}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {submitStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-[#037555]/10 rounded-full">
                    <i className="ri-check-line text-3xl text-[#037555]"></i>
                  </div>
                  <p className="text-lg font-medium text-gray-800 mb-2">送信完了しました</p>
                  <p className="text-sm text-gray-600">通常1営業日以内にご返信いたします。</p>
                </div>
              ) : (
                <form
                  id={activeForm === 'diagnosis' ? 'diagnosis-form' : 'consultation-form'}
                  data-readdy-form
                  onSubmit={(e) => handleSubmit(e, activeForm)}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      会社名 <span className="text-[#9a1e23]">*</span>
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      required
                      value={formData.companyName}
                      onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#12335f] focus:border-transparent"
                      placeholder="株式会社〇〇"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      お名前 <span className="text-[#9a1e23]">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#12335f] focus:border-transparent"
                      placeholder="山田 太郎"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      メールアドレス <span className="text-[#9a1e23]">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#12335f] focus:border-transparent"
                      placeholder="example@company.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      電話番号
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#12335f] focus:border-transparent"
                      placeholder="090-1234-5678"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {activeForm === 'diagnosis' ? '現在の課題・気になっていること' : 'ご相談内容'}
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) {
                          setFormData({ ...formData, message: e.target.value });
                        }
                      }}
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#12335f] focus:border-transparent resize-none"
                      placeholder="お気軽にご記入ください"
                    />
                    <p className="text-xs text-gray-500 mt-1 text-right">{formData.message.length}/500</p>
                  </div>

                  {submitStatus === 'error' && (
                    <p className="text-sm text-[#9a1e23]">送信に失敗しました。もう一度お試しください。</p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-4 bg-[#12335f] text-white text-base font-medium rounded-lg hover:bg-[#153577] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? '送信中...' : '送信する'}
                  </button>
                  <p className="text-xs text-gray-500 text-center">
                    ※通常1営業日以内にご返信いたします
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Service Areas Section */}
      <section id="areas" className="py-12 lg:py-16 bg-[#f8fafb]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-8">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#12335f] mb-4">
              対面伴走支援対応エリア
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {areas.map((area, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <span className="inline-block px-4 py-1.5 bg-[#037555] text-white text-sm font-medium rounded-full mb-4">
                  {area.region}
                </span>
                <p className="text-sm text-gray-700">
                  {area.prefectures.join(' / ')}
                </p>
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-[#037555]">
            ※ その他エリアはオンライン対応での対応となります
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#12335f] mb-4">
              よくある質問
            </h2>
            <p className="text-sm text-gray-600">
              お客様からよくいただくご質問にお答えします
            </p>
          </div>

          <div className="space-y-4">
            <FaqItem
              question="DXとは何ですか？難しそうで不安です。"
              answer="DX（デジタルトランスフォーメーション）とは、デジタル技術を活用して業務を効率化したり、新しい価値を生み出すことです。難しく考える必要はありません。例えば、紙の書類をデジタル化したり、SNSで集客したりすることもDXの一つです。私たちは専門用語を使わず、分かりやすくサポートいたします。"
            />
            <FaqItem
              question="ITに詳しくなくても大丈夫ですか？"
              answer="はい、全く問題ありません。むしろITに詳しくない方こそ、私たちのサービスをご活用いただきたいと考えています。専門用語を使わず、一つひとつ丁寧にご説明しながら進めていきます。パソコンやスマートフォンの基本操作ができれば十分です。"
            />
            <FaqItem
              question="どのくらいの期間で効果が出ますか？"
              answer="取り組む内容によって異なりますが、SNS運用であれば3〜6ヶ月程度で効果を実感いただけることが多いです。業務効率化については、導入直後から時間短縮を実感いただけます。焦らず、お客様のペースに合わせて進めていきますのでご安心ください。"
            />
            <FaqItem
              question="費用はどのくらいかかりますか？"
              answer="サービス内容やご要望によって異なります。まずは無料相談で現状をお聞かせいただき、最適なプランをご提案いたします。補助金・助成金の活用もサポートしておりますので、費用面でのご不安もお気軽にご相談ください。"
            />
            <FaqItem
              question="対面とオンライン、どちらで対応してもらえますか？"
              answer="四国エリア（高知・香川・愛媛・徳島）、広島市、岡山市、神戸市、大阪府、東京都は対面での伴走支援が可能です。その他のエリアはオンライン（Zoom等）での対応となります。オンラインでも対面と変わらない質のサポートを提供いたします。"
            />
            <FaqItem
              question="契約期間の縛りはありますか？"
              answer="最低契約期間は6ヶ月からとなっております。DXの取り組みは一定期間継続することで効果が現れるため、まずは6ヶ月間しっかりとサポートさせていただきます。その後は、お客様の状況に応じて柔軟に対応いたします。"
            />
          </div>
        </div>
      </section>

      {/* Company Info Section */}
      <section id="company" className="py-24 lg:py-32 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#12335f]">
              運営会社情報
            </h2>
          </div>

          <div className="bg-[#f8fafc] rounded-xl p-8 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              {/* 左側:会社情報 */}
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <span className="w-1 h-6 bg-[#9a1e23] rounded-full"></span>
                  <h3 className="text-lg font-bold text-[#12335f]">合同会社デジポップ</h3>
                </div>
                <div className="space-y-5">
                  <div className="flex">
                    <p className="text-sm text-gray-500 w-20 flex-shrink-0">会社名</p>
                    <p className="text-sm text-gray-800">合同会社デジポップ</p>
                  </div>
                  <div className="flex">
                    <p className="text-sm text-gray-500 w-20 flex-shrink-0">代表者</p>
                    <p className="text-sm text-gray-800">杉本 憲昭</p>
                  </div>
                  <div className="flex">
                    <p className="text-sm text-gray-500 w-20 flex-shrink-0">所在地</p>
                    <p className="text-sm text-gray-800">
                      高知県香美市土佐山田町西本町1丁目5番26号
                    </p>
                  </div>
                  <div className="flex">
                    <p className="text-sm text-gray-500 w-20 flex-shrink-0">事業内容</p>
                    <p className="text-sm text-[#9a1e23]">
                      SNS運用代行・デジタルマーケティング支援・DX支援・生成AI活用サポート
                    </p>
                  </div>
                </div>
              </div>

              {/* 右側:サービス特徴 */}
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <span className="w-1 h-6 bg-[#9a1e23] rounded-full"></span>
                  <h3 className="text-lg font-bold text-[#12335f]">サービス特徴</h3>
                </div>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#9a1e23]/10 rounded-lg flex-shrink-0">
                      <i className="ri-heart-fill text-lg text-[#9a1e23]"></i>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-800 mb-1">温かいサポート</p>
                      <p className="text-xs text-gray-500 leading-relaxed">どんな業種の経営者様にも安心してご利用いただけるコーチング型支援</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#9a1e23]/10 rounded-lg flex-shrink-0">
                      <i className="ri-team-fill text-lg text-[#9a1e23]"></i>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-800 mb-1">伴走型支援</p>
                      <p className="text-xs text-gray-500 leading-relaxed">お客様の自立を目指し、ノウハウ習得まで継続サポート</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#9a1e23]/10 rounded-lg flex-shrink-0">
                      <i className="ri-line-chart-fill text-lg text-[#9a1e23]"></i>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-800 mb-1">成果重視</p>
                      <p className="text-xs text-gray-500 leading-relaxed">リード獲得にフォーカスした実践的な運用</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
